<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { DriverStore } from '../pinia/Driver.js'

const { proxy } = getCurrentInstance()
const route = useRoute()
const router = useRouter()
const Driver = DriverStore()
const open = ref(false)
const state = ref('loading')
const copy = ref('Activating your account…')
let lastToken = ''
let inFlight = false

const title = computed(() =>
  state.value === 'loading'
    ? 'Activating account'
    : state.value === 'success'
      ? 'Account activated'
      : state.value === 'invalid'
        ? 'Link not valid'
        : 'Activation failed'
)
const icon = computed(() =>
  state.value === 'success'
    ? 'mdi-check-circle'
    : state.value === 'loading'
      ? null
      : 'mdi-alert-circle'
)

async function activate(token) {
  const value = String(token || '').trim()
  if (!value) {
    state.value = 'invalid'
    copy.value = 'This activation link is missing a token.'
    open.value = true
    return
  }
  if (inFlight && lastToken === value) return
  inFlight = true
  lastToken = value
  state.value = 'loading'
  copy.value = 'Activating your account…'
  open.value = true
  try {
    const result = await proxy.$api({
      serviceName: 'session',
      methodName: 'confirmAccount',
      parameters: [value],
    })
    if (result === true || result === 'true' || result === 1 || result === '1') {
      state.value = 'success'
      copy.value = 'Your account is activated. Connect on any launcher, go in-game and create your first driver before logging to the website.'
    } else {
      state.value = 'invalid'
      copy.value = 'This activation link is invalid or has already been used.'
    }
  } catch (error) {
    console.error('Account activation failed:', error)
    state.value = 'error'
    copy.value = 'Something went wrong while activating your account. Try again or request a new link.'
  } finally {
    inFlight = false
  }
}

function close() {
  if (state.value !== 'loading') open.value = false
}
function onModel(value) {
  if (value) open.value = true
  else close()
}
function openSignIn() {
  open.value = false
  Driver.showDrawer(true)
}
function resend() {
  open.value = false
  router.push('/resend-activation')
}

watch(
  () => [route.name, route.params.token],
  ([name, token]) => {
    if (name === 'Confirm' && token) {
      const value = String(token)
      if (route.path.startsWith('/confirm')) router.replace('/')
      activate(value)
    }
  },
  { immediate: true }
)
</script>

<template><v-dialog :model-value="open" width="560" scrim="rgba(0,0,0,0.72)" transition="dialog-bottom-transition" :persistent="state === 'loading'" @update:model-value="onModel">
    <v-card class="confirm-dialog-card">
      <header class="confirm-header">
        <div class="confirm-header-main">
          <div class="confirm-icon" :class="state">
            <v-progress-circular v-if="state === 'loading'" indeterminate size="22" width="2.5" />
            <v-icon v-else size="22">{{ icon }}</v-icon>
          </div>
          <div class="confirm-heading">
            <span class="confirm-kicker">Account</span>
            <h2>{{ title }}</h2>
          </div>
        </div>
        <button type="button" class="confirm-close" :disabled="state === 'loading'" aria-label="Close" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </header>
      <p class="confirm-copy">{{ copy }}</p>
      <div v-if="state !== 'loading'" class="confirm-actions">
        <v-btn v-if="state === 'success'" color="primary" variant="flat" @click="openSignIn"> Sign in </v-btn>
        <template v-else>
          <v-btn variant="outlined" @click="resend"> Resend email </v-btn>
          <v-btn variant="tonal" @click="activate(lastToken)"> Try again </v-btn>
        </template>
      </div>
    </v-card>
  </v-dialog></template>

<style scoped>
.confirm-dialog-card {
  overflow:hidden;
  border-radius:16px!important;
  border:1px solid var(--line-strong);
  background:var(--card-bg-solid)!important;
  color:var(--ink);
  padding:20px 20px 18px}
.confirm-header {
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px}
.confirm-header-main {
  display:flex;
  align-items:center;
  gap:12px;
  min-width:0}
.confirm-icon {
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:44px;
  height:44px;
  border-radius:12px;
  background:var(--glass-strong);
  color:var(--ink);
  flex-shrink:0}
.confirm-icon.success {
  color:var(--status-success, #66bb6a);
  background:color-mix(in srgb,var(--status-success, #66bb6a) 16%,transparent)}
.confirm-icon.invalid, .confirm-icon.error {
  color:var(--status-danger, #ef5350);
  background:color-mix(in srgb,var(--status-danger, #ef5350) 16%,transparent)}
.confirm-heading {
  min-width:0}
.confirm-kicker {
  display:block;
  margin-bottom:2px;
  font-size:.68rem;
  font-weight:800;
  letter-spacing:.12em;
  text-transform:uppercase;
  color:var(--mute)}
.confirm-heading h2 {
  margin:0;
  font-size:1.12rem;
  font-weight:800;
  letter-spacing:.02em;
  line-height:1.2;
  color:var(--ink)}
.confirm-close {
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:36px;
  height:36px;
  border-radius:10px;
  border:1px solid var(--line-strong);
  background:var(--glass);
  color:var(--ink-soft);
  cursor:pointer;
  flex-shrink:0}
.confirm-close:disabled {
  opacity:.4;
  cursor:not-allowed}
.confirm-close:hover:not(:disabled) {
  background:var(--glass-strong);
  color:var(--ink)}
.confirm-copy {
  margin:14px 0 0;
  font-size:.88rem;
  line-height:1.45;
  color:var(--mute);
  text-align:left}
.confirm-actions {
  display:flex;
  flex-wrap:wrap;
  justify-content:flex-end;
  gap:8px;
  margin-top:18px}
</style>
